import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

/**
 * One KPI square on the welcome screen. Flat, light and quiet on purpose — the
 * numbers carry the page, so the tiles stay out of their way.
 */
export function StatTile({
  label, value, hint, icon, delta, valueTitle, emphasis = false,
}: {
  label: string;
  value: string;
  hint?: string;
  icon: ReactNode;
  /** Signed percentage vs. the previous period; omitted when there is no base to compare. */
  delta?: number | null;
  valueTitle?: string;
  /** Highlights the tile the user should read first. */
  emphasis?: boolean;
}) {
  const hasDelta = typeof delta === 'number' && isFinite(delta);
  const up = hasDelta && (delta as number) >= 0;

  return (
    <div
      className={`rounded-xl border p-5 flex flex-col gap-3 transition-colors ${
        emphasis
          ? 'bg-brand-50/80 border-brand-200/80 shadow-xs'
          : 'bg-white border-stone-200/80 hover:border-stone-300 shadow-card'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-stone-500 leading-tight">
          {label}
        </span>
        <span className={`shrink-0 ${emphasis ? 'text-brand-600' : 'text-stone-400'}`}>{icon}</span>
      </div>

      <div>
        <div
          className="text-2xl leading-none font-bold tracking-tight text-stone-900 tabular-nums"
          title={valueTitle}
        >
          {value}
        </div>
        <div className="mt-2.5 flex items-center gap-2 min-h-[18px]">
          {hint && <span className="text-[11px] font-normal text-stone-500 truncate">{hint}</span>}
          {hasDelta && (
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold shrink-0 ${
                up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
              }`}
              title="Geçen aya göre değişim"
            >
              {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              %{Math.abs(delta as number)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
